import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {HorribleHeroesGameConfig} from "../HorribleHeroesGameConfig";
import {TimelineLite, TweenLite} from "gsap";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {HorribleHeroesParsedGameData} from "../server/data/HorribleHeroesParsedGameData";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {IReelStoppedData, ReelController} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {SpinReelEvent} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinReelEvent";
import {HorribleHeroesGameMode} from "../HorribleHeroesGameMode";
import {HorribleHerosScatterWildymbol} from "../symbols/HorribleHerosScatterWildymbol";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {SoundManager} from "../resource/sound/SoundManager";
import {HorribleHeroesGameModuleConfig} from "../HorribleHeroesGameModuleConfig";
import {HorribleHeroesSoundConfig} from "../HorribleHeroesSoundConfig";
import {HorribleHeroesReelSymbol} from "../symbols/HorribleHeroesReelSymbol";

export class HHRandomWildView extends BaseView {
    private _energy: PIXI.spine.Spine;
    private _greenBlasts: TimelineSprite[][] = [];
    private _strokeTexts: TimelineSprite[][] = [];
    private _randomStrokeTween: TweenLite;
    private _wildOverlays: PIXI.spine.Spine[][] = [];
    private _wildOverlaysPosition: number[][] = [];
    private _shakeStageTween: TweenLite;
    private _strokeTween: TweenLite;
    private _mode: string = "";
    private _reels: string[][] = [];
    private _strokeTl: TimelineLite;
    private _blastTl: TimelineLite;
    private _wildLandingSoundPlayed: boolean = false;
    private _expandedSymbol: string = "";
    private _blastPlayed: boolean = false;

    // #464
    get blastPlayed(): boolean {
        return this._blastPlayed;
    }

    // #503
    private static playToxicWildSound(i: number): void {
        if (i < 3) {
            SoundManager.playToxicWild();
        } else if (i === 3) {
            SoundManager.playToxicWild4();
        } else {
            SoundManager.playToxicWild5();
        }
    }

    public playWildLanding(wildPos: number[]): void {
        let wildOverlay: PIXI.spine.Spine = this._wildOverlays[wildPos[0] - 1][wildPos[1]];
        wildOverlay.updateTransform();
        wildOverlay.visible = true;
        wildOverlay.state.clearTracks();
        wildOverlay.skeleton.setSlotsToSetupPose();
        wildOverlay.skeleton.setToSetupPose();
        wildOverlay.state.setEmptyAnimation(0, 0);
        wildOverlay.state.setAnimation(0, "wildLanding", false);
    }

    public playAnimation(): void {
        this.removeWildOverlays();

        this._energy.updateTransform();
        this._energy.visible = true;

        this._energy.alpha = 0;
        TweenLite.to(this._energy, 0.2, {alpha: 1});

        this._energy.state.clearTracks();
        this._energy.skeleton.setSlotsToSetupPose();
        this._energy.skeleton.setToSetupPose();
        this._energy.state.setEmptyAnimation(0, 0);
        this._energy.state.setAnimation(0, "rw_edgeEnergy", true);

        // #503
        SoundManager.playToxicShuffle();

        this._randomStrokeTween = TweenLite.delayedCall(0.15, () => this.playRandomStrokeText());
    }

    // #430
    public replaceWildSymbolInFSLow(id: number): void {
        const reelSet: string[] = this._reels[id],
            reelController: ReelController = Reels.getReel(id)!;

        if (reelController) {
            // visible symbols only
            for (let i: number = 1; i < reelSet.length - 1; i++) {
                if (reelSet[i] === "W") {
                    const newSym: HorribleHerosScatterWildymbol = new HorribleHerosScatterWildymbol("W", id, HorribleHeroesGameConfig.instance.SYMBOL_STATES.normal),
                        currSym: HorribleHeroesReelSymbol = (reelController.allSymbols[i] as HorribleHeroesReelSymbol);

                    let wildOverlay: PIXI.spine.Spine;

                    if (currSym && currSym.symName === "L1") {
                        newSym.drawSymMask();
                        newSym.position.set(currSym.x, currSym.y);

                        // #247
                        currSym.stopWinFireAnimation();

                        reelController.removedChildOnView(currSym);

                        reelController.allSymbols.splice(i, 1, newSym);
                        reelController.addChildOnView(newSym);

                        // turn off the overlay wild
                        wildOverlay = this._wildOverlays[id - 1][i - 1];
                        wildOverlay.visible = false;
                        wildOverlay.state.clearTracks();
                    }
                }
            }
        }
    }

    public exitFS(): void {
        this.removeWildOverlays();
    }

    protected initAnimations(): void {
        super.initAnimations && super.initAnimations();

        StageManager.getLayer(HorribleHeroesGameConfig.instance.LAYERS.WILD_ENERGY.name).addChild(this);

        const numCols: number = 3,
            numRows: number = 4,
            strokeAssetName: string = "wildStrokeText",
            wildOverlayAssetName: string = "wildlanding",
            blastAssetName: string = "greenOverlay";
        let pos: number[],
            strokeText: TimelineSprite,
            wildOverlay: PIXI.spine.Spine,
            blast: TimelineSprite;

        this._energy = new PIXI.spine.Spine(GameResources.getSpineAsset("randomWildEdge"));
        this._energy.visible = false;
        this._energy.position.set(360);

        this.addChild(this._energy);

        for (let i: number = 0; i < numCols; i++) {
            this._strokeTexts[i] = [];
            this._wildOverlays[i] = [];
            this._greenBlasts[i] = [];

            for (let j: number = 0; j < numRows; j++) {
                pos = Helper.getSymbolPositions((i + 1), j);

                strokeText = new TimelineSprite(GameResources.getTextures(strokeAssetName));
                strokeText.hide();
                strokeText.anchor.set(0.5);
                strokeText.scale.set(2);
                strokeText.blendMode = PIXI.BLEND_MODES.ADD;
                strokeText.position.set(pos[0], pos[1]);

                wildOverlay = new PIXI.spine.Spine(GameResources.getSpineAsset(wildOverlayAssetName));
                wildOverlay.visible = false;
                wildOverlay.position.set(pos[0], pos[1]);

                blast = new TimelineSprite(GameResources.getTextures(blastAssetName));
                blast.hide();
                blast.anchor.set(0.5);
                blast.blendMode = PIXI.BLEND_MODES.ADD;
                blast.position.set(pos[0], pos[1]);

                this.addChild(strokeText, wildOverlay, blast);

                this._strokeTexts[i].push(strokeText);
                this._wildOverlays[i].push(wildOverlay);
                this._greenBlasts[i].push(blast);
            }
        }
    }

    protected addEventListeners(): void {
        super.addEventListeners && super.addEventListeners();

        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => this.onGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, SpinReelEvent.REEL_STOP_SPIN_BOUNCE_STARTED, (event: GameEvent) => this.onReelBounce(event.params[0]));
    }

    private onGameDataParsed(data: HorribleHeroesParsedGameData): void {
        this._wildOverlaysPosition = [];
        const randomWildFS: boolean[][] = data.randomWildFS;
        // restricting to middle 3 reels only
        for (let i: number = 1; i < randomWildFS.length - 1; i++) {
            for (let j: number = 0; j < randomWildFS[i].length; j++) {
                if (randomWildFS[i][j]) {
                    this._wildOverlaysPosition.push([i, j]);
                }
            }
        }

        TweenLite.delayedCall(1.5, () => this._randomStrokeTween && this._randomStrokeTween.kill());

        this._strokeTween = TweenLite.delayedCall(2, () => {
            let strokeText: TimelineSprite,
                wildPos: number[];

            this._strokeTl = new TimelineLite();
            for (let i: number = 0; i < this._wildOverlaysPosition.length; i++) {
                this._strokeTl.add(() => {
                    wildPos = this._wildOverlaysPosition[i];
                    strokeText = this._strokeTexts[wildPos[0] - 1][wildPos[1]];
                    strokeText.getAnimationAutoShowHide(true, true);
                }, (i * 0.4));
            }
        });
        this._blastPlayed = false;
        TweenLite.delayedCall(2.33, () => {
            if (!this._blastPlayed && this._energy.visible) {
                this.playBlast(true);
            }
        });

        this._mode = data.mode;
        this._reels = data.reels;

        // #441
        this._wildLandingSoundPlayed = false;

        // #466
        this._expandedSymbol = data.getExpandedSymbol;
    }

    private onReelBounce(data: IReelStoppedData): void {
        if (data.reelId === 0 && !this._blastPlayed && this._energy.visible) {
            this.playBlast(false);
            // #441
            if (!this._wildLandingSoundPlayed && data.quickStop) {
                this._wildLandingSoundPlayed = true;
                HHRandomWildView.playToxicWildSound((this._wildOverlaysPosition.length - 1));
            }
        }
        const reelId: number = data.reelId,
            lastReelId: number = HorribleHeroesGameConfig.instance.REELS_NUM - 1;
        // middle 3 reels only
        if (reelId > 0 && reelId < lastReelId) {
            if (this._mode === HorribleHeroesGameMode.NORMAL) {
                const reel: string[] = this._reels[reelId];
                // visible symbols only
                for (let i: number = 1; i < reel.length - 1; i++) {
                    if (reel[i] === "W") {
                        this.playWildLanding([reelId, (i - 1)]);

                        // #441
                        if (!data.quickStop) {
                            SoundManager.playWildLanding();
                        } else if (!this._wildLandingSoundPlayed && data.quickStop) {
                            this._wildLandingSoundPlayed = true;
                            SoundManager.playWildLanding();
                        }
                    }
                }
            }
        } else if (reelId === lastReelId) {
            if (this._strokeTl) {
                this._strokeTl.pause();
                this._strokeTl.kill();
            }
            if (this._blastTl) {
                // #466
                if (this._blastTl.totalProgress() > 0 && this._blastTl.totalProgress() < 1 && this._wildOverlaysPosition.length > 2) {
                    HorribleHeroesGameModuleConfig.logoAnimations.playAnimationsOnWin(this._mode, this._expandedSymbol);
                }

                this._blastTl.pause();
                this._blastTl.kill();
            }

            if (this._shakeStageTween && this._shakeStageTween.totalProgress() > 0 && this._shakeStageTween.totalProgress() < 1) {
                this._shakeStageTween.totalProgress(1);
            }

            if (this._mode === HorribleHeroesGameMode.NORMAL || this._mode === HorribleHeroesGameMode.FREESPIN_MID) {
                this.removeWildOverlays();
            }
        }
    }

    private playRandomStrokeText(): void {
        const colIdx: number = MathHelper.randomIntInRange(0, 2),
            rowIdx: number = MathHelper.randomIntInRange(0, 3);

        this._strokeTexts[colIdx][rowIdx].getAnimationAutoShowHide(true, true);

        this._randomStrokeTween = TweenLite.delayedCall(0.15, () => this.playRandomStrokeText());
    }

    private playBlast(canPlayWildLand: boolean): void {
        let blast: TimelineSprite,
            wildPos: number[];
        if (this._strokeTween) {
            this._strokeTween.kill();
        }
        if (this._wildOverlaysPosition.length > 0) {
            this._randomStrokeTween.kill();
        }
        this._blastTl = new TimelineLite();

        for (let i: number = 0; i < this._wildOverlaysPosition.length; i++) {
            this._blastTl.add(() => {
                wildPos = this._wildOverlaysPosition[i];
                if (canPlayWildLand) {
                    //will not play landing animation incase of quickStop
                    this.playWildLanding(wildPos);
                    // #503
                    HHRandomWildView.playToxicWildSound(i);
                    blast = this._greenBlasts[wildPos[0] - 1][wildPos[1]];
                    blast.scale.set(1.4);
                    TweenLite.to(blast.scale, 0.1, {x: 1, y: 1});
                    blast.getAnimationAutoShowHide(true, true);
                    this._shakeStageTween = TweenLite.to(StageManager.stage, 0.2, {y: "+=" + 15, ease: "shakeBounce"});
                }
                if (i === 0) {
                    //#572
                    this._blastPlayed = true;
                    this._energy.visible = false;
                    this._energy.state.clearTracks();
                    // #503
                    SoundManager.stopSound(HorribleHeroesSoundConfig.instance.TOXIC_SHUFFLE);
                }
                // #466
                if (i === 2) {
                    HorribleHeroesGameModuleConfig.logoAnimations.playAnimationsOnWin(this._mode, this._expandedSymbol);
                }
            }, (i * 0.4));
        }
    }

    private removeWildOverlays(): void {
        let wildOverlay: PIXI.spine.Spine;

        for (let i: number = 0; i < this._strokeTexts.length; i++) {
            for (let j: number = 0; j < this._strokeTexts[i].length; j++) {
                this._strokeTexts[i][j].hide();
                this._greenBlasts[i][j].hide();

                wildOverlay = this._wildOverlays[i][j];
                wildOverlay.visible = false;
                wildOverlay.state.clearTracks();
            }
        }
    }
}